import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Mail, Phone, MapPin, ArrowRight } from 'lucide-react';

const WHATSAPP_URL = `https://wa.me/447757233260?text=${encodeURIComponent(
    `Hi! I just submitted the contact form on your website. Looking forward to hearing from the Qorvix team.`
)}`;

export default function ContactPage() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        phone: '',
        service: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...formData,
                    timestamp: new Date().toISOString()
                })
            });

            if (response.ok) {
                setSubmitSuccess(true);
                // Force redirect to WhatsApp — navigation (not popup) is never blocked
                setTimeout(() => {
                    window.location.href = WHATSAPP_URL;
                }, 1500);
            } else {
                alert('Something went wrong. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            alert('Failed to submit. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    return (
        <div className="min-h-screen bg-white font-['Outfit',sans-serif] text-gray-900">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
        
        .text-gradient {
          background: linear-gradient(135deg, #FF6B35 0%, #FFA500 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

            {/* Navigation */}
            <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white'}`}>
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <Link to="/" className="flex items-center gap-2">
                            <img
                                src="/logo-qorvix.png"
                                alt="Qorvix Logo"
                                className="h-10 w-auto"
                            />
                        </Link>

                        <div className="hidden lg:flex items-center space-x-8">
                            <Link to="/" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Home</Link>
                            <Link to="/about" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">About</Link>
                            <Link to="/how-it-works" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">How it works</Link>
                            <Link to="/portfolio" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Portfolio</Link>
                            <div className="relative group">
                                <button className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px] flex items-center gap-1">
                                    Resources
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                                    <div className="py-2">
                                        <Link to="/documentation" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Documentation</Link>
                                        <Link to="/integrations" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Integration</Link>
                                        <Link to="/faq" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">FAQs</Link>
                                        <Link to="/help-center" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Help center</Link>
                                        <Link to="/roadmap" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Roadmap</Link>
                                    </div>
                                </div>
                            </div>
                            <Link to="/contact" className="text-orange-500 hover:text-orange-600 transition-colors font-medium text-[15px]">Contact us</Link>
                            <button onClick={() => {
                                setModalButtonType('demo');
                                setIsSignupModalOpen(true);
                            }} className="bg-orange-500 text-white px-6 py-2.5 rounded-lg hover:bg-orange-600 transition-all font-medium text-[15px]">
                                Start Free Demo
                            </button>
                        </div>

                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden text-gray-900"
                        >
                            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu Modal */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)}></div>
                    <div className="fixed top-8 left-1/2 -translate-x-1/2 w-[90%] max-w-sm bg-white rounded-3xl shadow-2xl p-8 z-50">
                        <button
                            onClick={() => setMobileMenuOpen(false)}
                            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                        >
                            <X size={24} />
                        </button>
                        <nav className="mt-4 space-y-6">
                            <Link to="/" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Home</Link>
                            <Link to="/about" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>About</Link>
                            <Link to="/how-it-works" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>How it works</Link>
                            <Link to="/portfolio" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>portfolio</Link>
                            <div>
                                <button className="w-full text-left text-lg font-medium text-gray-900 flex items-center gap-2">
                                    resources
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" className="mt-1">
                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <div className="mt-4 ml-4 space-y-4">
                                    <Link to="/documentation" className="block text-base text-gray-700 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Documentation</Link>
                                    <Link to="/integrations" className="block text-base text-gray-700 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Integrations</Link>
                                    <Link to="/faq" className="block text-base text-gray-700 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>FAQs</Link>
                                    <Link to="/help-center" className="block text-base text-gray-700 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Help center</Link>
                                    <Link to="/roadmap" className="block text-base text-gray-700 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Roadmap</Link>
                                </div>
                            </div>
                            <Link to="/contact" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Contact us</Link>
                            <button className="text-lg font-medium text-orange-500 border-b-2 border-orange-500 pb-1">Start free demo</button>
                        </nav>
                    </div>
                </div>
            )}

            {/* Hero Section with Background Image */}
            <section className="pt-32 pb-20 px-4 relative overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1920&h=600&fit=crop"
                        alt="Contact"
                        className="w-full h-full object-cover opacity-30"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-100/80 to-blue-200/80"></div>
                </div>
                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <h1 className="text-5xl md:text-6xl font-bold mb-6">
                        <span className="text-gradient">Contact Us</span>
                    </h1>
                    <p className="text-lg text-gray-700">
                        We are here for you! How can we help?
                    </p>
                </div>
            </section>

            {/* Contact Form Section */}
            <section className="py-20 px-4 bg-gray-50">
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-start">
                        {/* Form */}
                        <div className="bg-white rounded-3xl p-10 shadow-lg">
                            <h2 className="text-3xl font-bold mb-8">Get In Touch</h2>

                            {/* Success Message */}
                            {submitSuccess && (
                                <div className="mb-6 bg-green-50 border-2 border-green-500 text-green-800 px-6 py-4 rounded-lg flex items-start gap-3">
                                    <svg className="w-6 h-6 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <div>
                                        <p className="font-semibold">Message sent successfully!</p>
                                        <p className="text-sm">We'll get back to you within 24 hours.</p>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="relative">
                                    <input
                                        type="text"
                                        name="username"
                                        placeholder="Enter username here"
                                        value={formData.username}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-4 pr-12 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                                    />
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <path d="M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z" fill="currentColor" />
                                            <path d="M10 12C4.477 12 0 14.686 0 18V20H20V18C20 14.686 15.523 12 10 12Z" fill="currentColor" />
                                        </svg>
                                    </div>
                                </div>

                                <div className="relative">
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Enter your email address"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-4 pr-12 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                                    />
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                                        <Mail size={20} />
                                    </div>
                                </div>

                                <div>
                                    <input
                                        type="tel"
                                        name="phone"
                                        placeholder="phone number(Optional)"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="w-full px-4 py-4 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                                    />
                                </div>

                                <div className="relative">
                                    <select
                                        name="service"
                                        value={formData.service}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-4 pr-12 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors appearance-none text-gray-500"
                                    >
                                        <option value="">How can we help</option>
                                        <option value="general">General Inquiry</option>
                                        <option value="support">Technical Support</option>
                                        <option value="sales">Sales</option>
                                        <option value="partnership">Partnership</option>
                                    </select>
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>

                                <div>
                                    <textarea
                                        name="message"
                                        placeholder="Go ahead we are listening..."
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        rows="4"
                                        className="w-full px-4 py-4 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors resize-none"
                                    ></textarea>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-orange-500 text-white py-4 rounded-lg font-semibold hover:bg-orange-600 transition-all flex items-center justify-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? 'Sending...' : 'Submit'} {!isSubmitting && <ArrowRight size={20} />}
                                </button>
                            </form>
                        </div>

                        {/* Contact Info */}
                        <div className="space-y-6">
                            <div className="bg-orange-50 rounded-2xl p-6 flex items-start gap-4">
                                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Mail className="text-white" size={24} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg mb-1">Email Us</h3>
                                    <p className="text-gray-600">Our Support@qorvix.ai</p>
                                </div>
                            </div>

                            <div className="bg-orange-50 rounded-2xl p-6 flex items-start gap-4">
                                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Phone className="text-white" size={24} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg mb-1">Call Us</h3>
                                    <p className="text-gray-600">+44 7757 233260</p>
                                </div>
                            </div>

                            <div className="bg-orange-50 rounded-2xl p-6 flex items-start gap-4">
                                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <MapPin className="text-white" size={24} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg mb-1">Our Office</h3>
                                    <p className="text-gray-600">124 city road, London, ECIV 2NX</p>
                                </div>
                            </div>

                            {/* Social Media */}
                            <div className="flex gap-4 pt-4">
                                <a href="" className="w-12 h-12 bg-black rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                    </svg>
                                </a>
                                <a href="" className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center hover:opacity-90 transition-opacity">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                    </svg>
                                </a>
                                <a href="" className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white py-12 px-4 border-t border-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <h3 className="text-xl font-bold mb-4">
                                <span className="text-gradient">Qorvix</span> — Automate What Matters
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed mb-6">
                                Qorvix helps businesses automate workflows, connect tools, and scale faster using AI-powered systems. From CRM integrations to WhatsApp automations — we build smart, no-code solutions that work.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Contact</h4>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p>London, England, UK</p>
                                <p>+44 7757 233260</p>
                                <p>Support@qorvix.ai</p>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Company</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/" className="block text-gray-600 hover:text-gray-900 transition-colors">Home</Link>
                                <Link to="/about" className="block text-gray-600 hover:text-gray-900 transition-colors">About</Link>
                                <Link to="/portfolio" className="block text-gray-600 hover:text-gray-900 transition-colors">Portfolio</Link>
                                <Link to="/contact" className="block text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Resources</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/documentation" className="block text-gray-600 hover:text-gray-900 transition-colors">Documentation</Link>
                                <Link to="/integrations" className="block text-gray-600 hover:text-gray-900 transition-colors">Integration</Link>
                                <Link to="/faq" className="block text-gray-600 hover:text-gray-900 transition-colors">FAQs</Link>
                                <Link to="/help-center" className="block text-gray-600 hover:text-gray-900 transition-colors">Help Center</Link>
                                <Link to="/roadmap" className="block text-gray-600 hover:text-gray-900 transition-colors">Roadmap</Link>
                            </div>
                        </div>
                    </div>

                    <div className="pt-8 flex items-center justify-between border-t border-gray-200">
                        <div className="text-2xl font-bold text-gradient">Qorvix</div>
                        <div className="text-sm text-gray-600">
                            © Qorvix Technologies. All Rights Reserved. Built with purpose and precision by <span className="font-semibold">Qorvix Team</span>.
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}